import { create } from "zustand";
import axios from "axios";

// V2 API URL (SMS 등 마이그레이션 완료된 기능)
const V2_API_URL = `${import.meta.env.VITE_API_V2_URL || 'http://localhost:8091/api/v1'}`;

// V2 API 인스턴스 - SMS 등 마이그레이션 완료된 기능용
const api = axios.create({
	baseURL: V2_API_URL,
});
api.interceptors.request.use(
	(config) => {
		const token = JSON.parse(localStorage.getItem("auth-storage"))?.state?.token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// 기존 backend-api URL (v2 미구현 기능용)
const LEGACY_API_URL = `${import.meta.env.VITE_SERVER_URL}`;

// Legacy API 인스턴스 - 좌석배정, 예약 등 v2 미구현 기능용
const legacyApi = axios.create({
	baseURL: LEGACY_API_URL,
});
legacyApi.interceptors.request.use(
	(config) => {
		const token = JSON.parse(localStorage.getItem("auth-storage"))?.state?.token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

export const useEventStore = create((set, get) => ({
	// 날짜 범위로 행사 목록 조회 (V2 API - 좌석배정 페이지용)
	getEventsByDate: async (startDate, endDate, bound) => {
		set({ isLoading: true, error: null });
		try {
			const params = {
				start_date: startDate,
				end_date: endDate,
			};
			if (bound) {
				params.bound = bound;
			}
			const response = await api.get("/events/by-date", {
				params,
			});
			const data = response.data;
			set({ isLoading: false });
			// V2 응답 형식: { items: [...], total: number }
			return { success: true, data: data.items || [] };
		} catch (err) {
			console.error("날짜별 행사 목록 조회 중 오류 발생:", err);
			const errorMessage =
				err.response?.data?.detail || err.message || "행사 목록을 가져오는 중 오류가 발생했습니다.";
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// SMS 로그 조회 (v2 API)
	getSmsLogs: async (startDate, endDate) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.get("/sms", {
				params: {
					start_date: startDate,  // v2는 YYYY-MM-DD 형식
					end_date: endDate,
				},
			});

			// v2 응답: { items: [...], total: number }
			const data = response.data.items || response.data;

			set({
				smsLogs: data,
				isLoading: false,
			});

			return { success: true, data: data };
		} catch (err) {
			console.error("Error fetching SMS logs:", err);
			const errorMessage =
				err.response?.data?.detail || err.message || "SMS 로그를 가져오는 중 오류가 발생했습니다.";
			set({
				isLoading: false,
				error: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	},

	// SMS 로그 상세 조회1 - 행사 정보 조회 (v2 API)
	getSmsEventDetail: async (EV_YM, EV_SEQ, sms_id) => {
		set({ isLoading: true, error: null });

		try {
			const response = await api.get(`/sms/log/${sms_id}`, {
				params: {
					EV_YM: EV_YM,
					EV_SEQ: EV_SEQ,
				},
			});

			// v2 응답: { items: [...] }
			const data = response.data.items || response.data;
			set({ isLoading: false });
			return { success: true, data };
		} catch (err) {
			console.error("Error fetching SMS event detail:", err);
			set({
				isLoading: false,
				error:
					err.response?.data?.detail || err.message ||
					"SMS 이벤트 상세 정보를 가져오는 중 오류가 발생했습니다.",
			});
			return { success: false, error: err.message };
		}
	},

	// SMS 로그 상세 조회2 - 티켓 정보 조회 (기존 API - v2 미구현)
	getTicketSchedules: async (userId) => {
		set({ isLoading: true, error: null });

		try {
			const response = await legacyApi.get("/ticket/assign", {
				params: {
					userId: userId,
				},
			});

			const data = await response.data;

			set({ isLoading: false });
			return { success: true, data };
		} catch (err) {
			console.error("Error fetching ticket schedules:", err);
			set({
				isLoading: false,
				error:
					err.message ||
					"티켓 일정 정보를 가져오는 중 오류가 발생했습니다.",
			});
			return { success: false, error: err.message };
		}
	},

	// SMS 로그 상세 조회3 - 일정 정보 조회 (V2 API)
	getSchedules: async (EV_YM, EV_SEQ) => {
		set({ isLoading: true, error: null });

		try {
			const response = await api.get("/schedules", {
				params: {
					EV_YM: EV_YM,
					EV_SEQ: EV_SEQ,
				},
			});

			// V2 API는 { items: [...] } 형식으로 반환
			const data = response.data.items || [];
			set({ isLoading: false });
			return { success: true, data };
		} catch (err) {
			console.error("Error fetching schedules:", err);
			set({
				isLoading: false,
				error:
					err.message ||
					"일정 정보를 가져오는 중 오류가 발생했습니다.",
			});
			return { success: false, error: err.message };
		}
	},

	// SMS 발송 기능 (v2 API)
	sendSms: async (smsData) => {
		set({ isLoading: true, error: null });

		try {
			const response = await api.post("/sms/send", smsData);

			const data = response.data;
			set({ isLoading: false });
			return { success: true, data };
		} catch (err) {
			console.error("Error sending SMS:", err);
			set({
				isLoading: false,
				error: err.response?.data?.detail || err.message || "SMS 발송 중 오류가 발생했습니다.",
			});
			return { success: false, error: err.response?.data?.detail || err.message };
		}
	},

	// 예약건별 목록 조회 (기존 API - v2와 파라미터 상이)
	getReservations: async (startDate, endDate, searchText = "") => {
		set({ isLoading: true, error: null });

		try {
			// 쿼리 스트링 생성
			const queryParams = new URLSearchParams({
				start_date: startDate,
				end_date: endDate,
				search_text: searchText,
			}).toString();

			const response = await legacyApi.get(`/reservation?${queryParams}`);

			const data = await response.data;
			set({ isLoading: false });
			return { success: true, data };
		} catch (err) {
			console.error("Error fetching reservations:", err);
			set({
				isLoading: false,
				error:
					err.message ||
					"예약 정보를 가져오는 중 오류가 발생했습니다.",
			});
			return { success: false, error: err.message };
		}
	},

	// 예약건별 배정 정보 상세 조회 (기존 API - v2와 엔드포인트 상이)
	getReservationDetails: async (userId) => {
		set({ isLoading: true, error: null });

		try {
			const response = await legacyApi.get(
				`/reservation/getReservationDetails?user_id=${userId}`
			);

			const data = await response.data;
			set({ isLoading: false });
			return data;
		} catch (err) {
			console.error("Error fetching reservation details:", err);
			set({
				isLoading: false,
				error:
					err.message ||
					"예약 상세 정보를 가져오는 중 오류가 발생했습니다.",
			});
			return { success: false, error: err.message };
		}
	},

	// 선택한 좌석 배정 취소 (기존 API - v2와 요청 형식 상이)
	cancelSelectedSeats: async (userId, seatIds) => {
		set({ isLoading: true, error: null });

		try {
			// 제공된 코드 형식에 맞게 요청 데이터 구조 변경
			const assignInfos = seatIds.map((seatId) => ({
				seatId: seatId.toString(),
				userId: userId,
			}));

			const requestBody = {
				assignInfos: assignInfos,
				isOutbound: true, // 필요에 따라 동적으로 설정 가능
			};

			const response = await legacyApi.post(
				"/ticket/assign/cancel",
				requestBody
			);

			const data = await response.data;
			set({ isLoading: false });
			return { success: true, data };
		} catch (err) {
			console.error("좌석 배정 취소 중 오류 발생:", err);
			set({
				isLoading: false,
				error: err.message || "좌석 배정 취소 중 오류가 발생했습니다.",
			});
			return { success: false, error: err.message };
		}
	},

	// 보유 좌석 조회 API (V2 마이그레이션 완료)
	getHoldSeats: async (gubunType, searchDate) => {
		try {
			const response = await api.get("/seats/hold-seats", {
				params: {
					search_date: searchDate,
					bound: gubunType === "go" ? "go" : "back",
				},
			});

			const data = response.data;
			// V2 응답 형식: { items: [...], total_count, assigned_count, available_count }
			// Legacy 응답 형식과 호환되도록 변환
			const tickets = (data.items || []).map(item => ({
				id: item.seat_info_id,
				ticket_item_id: item.ticket_item_id,
				train_code: item.train_code,
				train_car_number: item.train_car_number,
				seat_code: item.seat_code,
				user_id: item.user_id,
				is_assigned: item.is_assigned,
			}));
			return { success: true, data: tickets };
		} catch (err) {
			console.error("보유 좌석 정보 조회 중 오류 발생:", err);
			return { success: false, error: err.message };
		}
	},

	// 엔진 조회 API (V2 마이그레이션 완료)
	getEngines: async () => {
		try {
			const response = await api.get(`/config/engines`);
			const data = response.data;
			// V2 응답 형식: { items: [...], total: number }
			// Legacy 응답 형식과 호환되도록 변환 (대문자 필드명)
			const engines = (data.items || []).map(item => ({
				...item,
				SEQ_NUM: item.seq_num,
				ENGINE_VER: item.engine_ver,
				DISP_NAME: item.disp_name,
			}));
			return { success: true, data: engines };
		} catch (err) {
			console.error("엔진 정보 조회 중 오류 발생:", err);
			return { success: false, error: err.message };
		}
	},

	// 열차 및 좌석 현황 조회 (V2 마이그레이션 완료)
	getTrainList: async (date) => {
		try {
			const response = await api.get("/trains", {
				params: { date },
			});

			const data = response.data;
			return { success: true, data: data.items || [] };
		} catch (err) {
			console.error("열차 및 좌석 현황 조회 중 오류 발생:", err);
			return { success: false, error: err.message };
		}
	},

	// 좌석배치도 레이아웃 목록 조회 (V2)
	getSeatmapLayouts: async () => {
		try {
			const response = await api.get("/seatmaps/layouts");
			const data = response.data;
			return { success: true, data: data.items || [] };
		} catch (err) {
			console.error("좌석배치도 레이아웃 조회 중 오류 발생:", err);
			return { success: false, error: err.message };
		}
	},

	// 행사 별 배정된 팀 조회 (V2 마이그레이션 완료)
	getReservationInfo: async (EV_YM, EV_SEQ, gubun_type) => {
		try {
			const response = await api.get(`/events/reservation-info`, {
				params: {
					ev_ym: EV_YM,
					ev_seq: EV_SEQ,
					bound: gubun_type === "go" ? "go" : "back",
				},
			});
			const data = response.data;
			// V2 응답 형식: { items: [...], total: number }
			// Legacy 응답 형식과 호환되도록 변환 (객체 형식)
			const legacyFormat = (data.items || []).map(item => ({
				RES_NM: item.res_nm,
				HANDPHONE1: item.phone?.split("-")[0] || "",
				HANDPHONE2: item.phone?.split("-")[1] || "",
				HANDPHONE3: item.phone?.split("-")[2] || "",
				RES_DAY: item.res_day,
				RES_SEQ: item.res_seq,
				EV_SEQ: item.ev_seq,
				SEAT_NM_G: item.seat_nm_g,
				SEAT_NM_R: item.seat_nm_r,
				TOTAL_CNT: item.total_cnt,
				SEAT_INFO_LIST: item.seat_assignments || [],
				user_id: item.user_id,
			}));
			return { success: true, data: legacyFormat };
		} catch (err) {
			console.error("행사별 배정된 팀 조회 중 오류 발생:", err);
			return { success: false, error: err.message };
		}
	},

	// 좌석 배치 실행 (V2 마이그레이션 완료)
	runSeatAssignment: async (params) => {
		try {
			const {
				date,
				trainCode,
				tableId,
				seatmapLayoutId,
				trainTypeCode,
				seatmapVariant,
				ctmStr,
				engVer,
				ticketItemIds = "",
			} = params;

			// ctmStr 파싱하여 customers 배열로 변환
			// ctmStr 형식: "{ticket_id}@@{event_id}@@{name}@@{phone_last4}@@{table_id},{인원수}///{...}"
			console.log("[DEBUG] runSeatAssignment params:", params);
			console.log("[DEBUG] ctmStr:", ctmStr);
			const customers = ctmStr.split("///").filter(Boolean).map(c => {
				const [info, count] = c.split(",");
				const [ticketId, eventId, name, phoneLast4, tblId] = info.split("@@");
				console.log("[DEBUG] Parsed customer:", { ticketId, eventId, name, phoneLast4, tblId, count });
				return {
					ticket_id: ticketId,
					event_id: eventId,
					name: name,
					phone: phoneLast4 || "",
					table_id: tblId || tableId,
					count: parseInt(count) || 0,
					seat_info_ids: [],
				};
			});

			const requestBody = {
				date: date,
				train_code: trainCode,
				table_id: tableId || null,
				seatmap_layout_id: seatmapLayoutId || null,
				train_type_code: trainTypeCode || null,
				seatmap_variant: seatmapVariant || null,
				ticket_item_ids: ticketItemIds ? ticketItemIds.split(",").map(Number).filter(Boolean) : [],
				customers: customers,
				engine_version: String(engVer || "8"),
			};
			console.log("[DEBUG] API Request Body:", JSON.stringify(requestBody, null, 2));

			// V2 API 호출
			const response = await api.post("/seats/run-assignment", requestBody);

			const data = response.data;
			// V2 응답의 seat_status + seatmap_layout 반환
			return {
				success: data.success,
				message: data.message || "",
				data: {
					seatStatus: data.seat_status || {},
					seatmapLayout: data.seatmap_layout || null,
				},
			};
		} catch (err) {
			console.error("좌석 배치 실행 중 오류 발생:", err);
			return { success: false, error: err.response?.data?.message || err.message };
		}
	},

	// 좌석 배치 결과 확정 (V2 마이그레이션 완료)
	confirmSeatAssignment: async (assignmentData) => {
		try {
			// assignmentData 형식: { assignInfos: [{seatId, userId}], isOutbound: boolean, evYm?, evSeq? }
			const response = await api.post("/seats/confirm-assignment", {
				assign_infos: assignmentData.assignInfos || [],
				ev_ym: assignmentData.evYm || assignmentData.ev_ym || "",
				ev_seq: assignmentData.evSeq || assignmentData.ev_seq || 0,
				is_outbound: assignmentData.isOutbound !== undefined ? assignmentData.isOutbound : true,
			});

			const data = response.data;
			return { success: data.success !== false, data: data };
		} catch (err) {
			console.error("좌석 배치 확정 중 오류 발생:", err);
			return { success: false, error: err.response?.data?.error || err.message };
		}
	},
}));


/**
 * 유틸리티 함수들
 */
export const SeatAssignmentUtils = {
	/**
	 * 날짜 형식 변환 (YYYYMMDD → YYYY-MM-DD)
	 * @param {string} dateStr - 변환할 날짜 문자열
	 * @returns {string} 변환된 날짜 문자열
	 */
	formatDateString(dateStr) {
	  if (!dateStr) return '';
	  
	  if (dateStr.includes('-')) {
		return dateStr; // 이미 형식화됨
	  }
	  
	  if (dateStr.length === 8) {
		const year = dateStr.substring(0, 4);
		const month = dateStr.substring(4, 6);
		const day = dateStr.substring(6, 8);
		return `${year}-${month}-${day}`;
	  }
	  
	  return dateStr;
	},
  
	/**
	 * 좌석 코드 시스템 확인
	 * @param {Object} seats - 좌석 데이터
	 * @returns {boolean} 숫자 시스템 여부
	 */
	isNumericSeatSystem(seats) {
	  const firstSeatCode = Object.keys(seats)[0];
	  return firstSeatCode && !isNaN(firstSeatCode);
	},
  
	/**
	 * 좌석 행 번호 계산
	 * @param {string} seatCode - 좌석 코드
	 * @param {boolean} isNumeric - 숫자 시스템 여부
	 * @returns {number} 행 번호
	 */
	calculateRowNumber(seatCode, isNumeric) {
	  if (isNumeric) {
		return Math.floor((parseInt(seatCode) - 1) / 4) + 1;
	  } else {
		return parseInt(seatCode.slice(0, -1));
	  }
	},
  
	/**
	 * 좌석 테이블 콘텐츠 파싱
	 * @param {string} tableContent - 테이블 콘텐츠 JSON 문자열
	 * @returns {Array} 파싱된 배치 정보
	 */
	parseTableContent(tableContent) {
	  try {
		const content = JSON.parse(tableContent);
		const assignInfos = [];
  
		content.forEach(row => {
		  const assignment = row[0].split(':');
		  if (assignment[1] !== undefined) {
			assignInfos.push({
			  seatId: assignment[0],
			  userId: assignment[1],
			});
		  }
		});
  
		return assignInfos;
	  } catch (error) {
		console.error('테이블 콘텐츠 파싱 실패:', error);
		return [];
	  }
	},
  
	/**
	 * 팀별 카운트 계산
	 * @param {Object} seatData - 좌석 데이터
	 * @returns {Object} 팀별 카운트 정보
	 */
	calculateTeamCounts(seatData) {
	  const teamCounts = {};
	  const teamCurrentCounts = {};
  
	  Object.values(seatData).forEach(carData => {
		Object.values(carData.data).forEach(seatInfo => {
		  if (seatInfo.seat && seatInfo.seat !== '') {
			const userId = seatInfo.seat.split('@@')[0];
			if (!teamCounts[userId]) {
			  teamCounts[userId] = seatInfo.total_cnt || 0;
			  teamCurrentCounts[userId] = 0;
			}
		  }
		});
	  });
  
	  return { teamCounts, teamCurrentCounts };
	},
  };
